import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectPracticeBannerHidden } from "features/app-layout/quick-launcher";
import {
  logoutUser,
  selectCurrentUser,
  selectCurrentUserToken
} from "features/auth/auth-slice";

import PracticeSelector from "../layout-top-bar/practice-selector/practice-selector.component";
import useSwitchPractice from "../layout-top-bar/practice-selector/use-switch-practice.hook";
import useActiveSessionContext from "./use-active-session-context.hook";

/**
 * Persistent context header for multi-practice users. Hosts the practice
 * switcher so the regular nav bar stays uncluttered. Two personas:
 *  - Internal Suno staff / break-glass → RED. Labelled "Admin" at the home
 *    practice, "Admin Mode" once switched into a foreign practice.
 *  - Franchise corporate (grant) users → NEUTRAL "Viewing".
 *
 * The header is always shown for multi-practice users (so they can start a
 * switch from home), unless they have hidden it from the quick launcher. Exit
 * (only while switched) returns to the home practice; a break-glass session —
 * which can't switch — is ended instead.
 */
export default function PracticeContextBanner() {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectCurrentUserToken);
  const currentUser = useAppSelector(selectCurrentUser);
  const isHidden = useAppSelector(selectPracticeBannerHidden);
  const {
    isSwitched,
    isInternal,
    isBreakGlass,
    activePracticeId,
    homePracticeId
  } = useActiveSessionContext();
  const { switchToPractice, isLoading } = useSwitchPractice();

  // Only multi-practice users get the header (and the switcher inside it).
  if (!currentUser?.is_multi_practice_user) return null;
  // Hidden from the quick launcher ("Hide practice switcher") — a per-browser
  // preference that survives reloads.
  //
  // Honoured while switched too, which means this header is not a guaranteed
  // indicator of the active practice. Nothing else in the shell reliably names
  // the practice (`CurrentClinicRegionSelector` shows region + clinic, and only
  // concatenates the practice name into `clinic.fullName` when regions are off
  // AND the viewport is >= xl, and not for a clinic labelled by its display name under
  // CLINIC_SELECTOR_DISPLAY_NAME_FEATURE), so the quick launcher carries both replacements:
  // it names the active practice in its footer and offers "Exit admin mode"
  // while switched. Keep those two in place if you change anything here.
  if (isHidden) return null;

  const handleExit = () => {
    if (isBreakGlass) {
      // Break-glass sessions cannot switch back (the backend rejects it); end
      // the session instead.
      if (token) dispatch(logoutUser({ token }));
      return;
    }
    if (homePracticeId != null) {
      switchToPractice(homePracticeId, activePracticeId ?? undefined);
    }
  };

  const prefix = isSwitched
    ? isInternal
      ? "Admin Mode"
      : "Viewing"
    : isInternal
      ? "Admin"
      : null;

  return (
    <Box
      // Internal/admin context is safety-critical — announce it assertively;
      // the neutral franchise context is polite.
      role={isInternal ? "alert" : "status"}
      data-testid="practice-context-banner"
      data-admin-mode={isInternal}
      data-switched={isSwitched}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        px: 2,
        py: 0.5,
        minHeight: 36,
        bgcolor: isInternal
          ? "error.main"
          : (theme) => alpha(theme.palette.info.main, 0.12),
        color: isInternal ? "error.contrastText" : "text.primary",
        borderBottom: isInternal
          ? "none"
          : (theme) => `1px solid ${alpha(theme.palette.info.main, 0.3)}`
      }}
    >
      {prefix && (
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ whiteSpace: "nowrap" }}
        >
          {prefix} ·
        </Typography>
      )}
      <PracticeSelector />
      {isSwitched && (
        <Button
          size="small"
          variant={isInternal ? "outlined" : "text"}
          onClick={handleExit}
          disabled={isLoading}
          data-testid="practice-context-exit"
          aria-label={isInternal ? "Exit admin mode" : "Exit practice view"}
          sx={{
            position: "absolute",
            right: 12,
            minHeight: 28,
            px: 1.25,
            // White on the red admin header; default text colour on neutral.
            color: isInternal ? "common.white" : "text.primary",
            borderColor: isInternal
              ? (theme) => alpha(theme.palette.common.white, 0.7)
              : undefined,
            "&:hover": isInternal
              ? {
                  borderColor: "common.white",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.common.white, 0.12)
                }
              : undefined
          }}
        >
          Exit
        </Button>
      )}
    </Box>
  );
}
